// STEP LIVE-CONSOLE-ROLE-SPLIT-UX-1 + FINAL-UI-POLISH-1 + FINAL-VISUAL-MATCH-1 ·
// 방송 모니터 Card.
//
// 책임:
//   · 판매자가 외부 LIVE 플랫폼 (YouTube · TikTok · Instagram) + 자사 LIVE (LIVEMOA LIVE)
//     실제 방송 화면을 **새 창** 으로 열어 모니터링 (지시 §7 · §12 · §13 · §14).
//   · 미니주문-only 판매자: LiveKit/Egress 를 강제하지 않고 외부 LIVE URL 을
//     seller 단위 localStorage 로 저장 (지시 §8 · 미니주문-only fallback).
//   · 실제 LIVEMOA REAL 송출 기능이 없는 플랫폼은 "미연동" 라벨로 표시 (지시 §9 ·
//     Mock 을 Production UI 처럼 과장 금지).
//   · 신규 backend endpoint / collection 추가 금지 — localStorage 저장만.
//
// FINAL-VISUAL-MATCH-1 (2026-10-07):
//   · 4-card 구조: LIVEMOA LIVE + YouTube + TikTok + Instagram (지시 §12 §13 §14).
//   · Desktop 4-column · 중폭 2×2 · Mobile 2-column 또는 stack · horizontal overflow 금지.
//   · LIVEMOA LIVE 는 소비자 LIVE route (`/live/{scheduleId}`) 새 창 — Camera/Mic 금지
//     (지시 §13 §15).  scheduleId 없을 때 '방송 선택 필요' 로 안전 표시.
//
// 저장 key:
//   livemoa:seller-live-control:external-monitor-urls:{seller_id}
//     = { youtube?: string, tiktok?: string, instagram?: string }
import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Youtube, Edit3, Save, X as Close, Music, Instagram, Radio } from 'lucide-react';


const _STORAGE_KEY_PREFIX = 'livemoa:seller-live-control:external-monitor-urls';
const _storageKey = (sellerId) => `${_STORAGE_KEY_PREFIX}:${sellerId || 'anon'}`;

// 지시 §9 · Mock 을 Production UI 처럼 과장 금지 — URL 미등록 시 명시 표기.
const _EMPTY_LABEL = 'LIVE 주소 미등록';
const _LINKED_LABEL = 'LIVE 주소 연결됨';


const _PLATFORMS = [
  { key: 'youtube',   label: 'YouTube',   hint: 'https://youtube.com/watch?v=...',       Icon: Youtube,   accent: '#FF0033' },
  { key: 'tiktok',    label: 'TikTok',    hint: 'https://www.tiktok.com/@user/live',     Icon: Music,     accent: '#000000' },
  { key: 'instagram', label: 'Instagram', hint: 'https://www.instagram.com/user/live/',  Icon: Instagram, accent: '#E1306C' },
];


const _safeOpen = (url) => {
  if (!url) return;
  try {
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (w && typeof w.opener !== 'undefined') { try { w.opener = null; } catch {} }
  } catch { /* popup blocked */ }
};


export default function BroadcastMonitorCard({ sellerId, youtubeAutoUrl = '', scheduleId = '', livemoaStatus = null, onOpenLivemoa = null, livemoaTest = false }) {
  const [urls, setUrls] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(_storageKey(sellerId));
      setUrls(raw ? (JSON.parse(raw) || {}) : {});
    } catch { setUrls({}); }
  }, [sellerId]);

  const persist = useCallback((next) => {
    try { window.localStorage.setItem(_storageKey(sellerId), JSON.stringify(next)); } catch {}
  }, [sellerId]);

  const effectiveUrl = useCallback((key) => {
    // YouTube 는 LIVEMOA 가 자동 확보한 URL 이 있으면 그걸 우선 사용 (지시 §8-A REUSE).
    if (key === 'youtube' && youtubeAutoUrl) return youtubeAutoUrl;
    return (urls[key] || '').trim();
  }, [urls, youtubeAutoUrl]);

  const beginEdit = (key) => {
    setEditingKey(key);
    setDraft(urls[key] || '');
  };
  const cancelEdit = () => { setEditingKey(null); setDraft(''); };
  const saveEdit = () => {
    const next = { ...urls, [editingKey]: draft.trim() };
    if (!next[editingKey]) delete next[editingKey];
    setUrls(next);
    persist(next);
    cancelEdit();
  };

  // LIVEMOA LIVE 자사 소비자 route REUSE — 새 창.  신규 player 금지 (지시 §13).
  const livemoaUrl = scheduleId ? `/live/${scheduleId}` : '';
  const livemoaLive = livemoaStatus === 'live' || livemoaStatus === '라이브';

  return (
    <section className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-3"
             data-testid="broadcast-monitor-card">
      <header className="flex items-baseline gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black"
              data-testid="section-badge-5">5</span>
        <h3 className="text-xs font-black inline-flex items-center gap-1">
          방송 모니터
          <span className="text-[10px] font-medium text-gray-500 ml-1">(외부 LIVE 시청)</span>
        </h3>
      </header>
      <p className="text-[10px] text-gray-500 leading-snug mb-2"
         data-testid="broadcast-monitor-hint">
        각 플랫폼의 실제 방송 페이지를 새 창으로 열어 시청할 수 있습니다.
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {/* LIVEMOA LIVE — 자사 LIVE 보기 (지시 §12 §13).  Camera/Mic 금지 · 소비자 route REUSE. */}
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-2 flex flex-col gap-1.5 bg-black/[0.015] dark:bg-white/[0.02]"
             data-testid="broadcast-monitor-livemoa"
             data-has-url={livemoaUrl ? 'true' : 'false'}>
          <div className="inline-flex items-center gap-1.5">
            <Radio size={14} className="text-[#FF3B30]" />
            <span className="text-[11px] font-black">LIVEMOA LIVE</span>
          </div>
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${livemoaLive ? 'text-[#FF3B30]' : 'text-gray-400'}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${livemoaLive ? 'bg-[#FF3B30] animate-pulse' : 'bg-gray-300 dark:bg-white/20'}`} />
            {livemoaTest ? 'TEST 미리보기' : livemoaLive ? '방송중' : (scheduleId ? '대기' : '방송 선택 필요')}
          </span>
          <button type="button"
                  onClick={() => (onOpenLivemoa ? onOpenLivemoa() : _safeOpen(livemoaUrl))}
                  disabled={!onOpenLivemoa && !livemoaUrl}
                  className="px-2 py-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold inline-flex items-center justify-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="broadcast-monitor-open-livemoa">
            자사 LIVE 보기 <ExternalLink size={10} />
          </button>
          <span className="text-[9px] text-gray-400 leading-tight">{livemoaTest ? 'TEST 소비자 화면 (실제 송출 아님)' : '소비자 라이브 화면'}</span>
        </div>

        {_PLATFORMS.map(({ key, label, hint, Icon, accent }) => {
          const url = effectiveUrl(key);
          const editing = editingKey === key;
          return (
            <div key={key}
                 className="rounded-xl border border-black/10 dark:border-white/10 p-2 flex flex-col gap-1.5 bg-black/[0.015] dark:bg-white/[0.02]"
                 data-testid={`broadcast-monitor-row-${key}`}
                 data-has-url={url ? 'true' : 'false'}>
              <div className="inline-flex items-center gap-1.5">
                <Icon size={14} style={{ color: accent }} />
                <span className="text-[11px] font-black">{label}</span>
              </div>
              {editing ? (
                <>
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={hint}
                    className="w-full px-2 py-1 rounded-full border border-black/10 dark:border-white/10 text-[11px] bg-white dark:bg-black/40 focus:outline-none focus:border-[#FF3B30]"
                    data-testid={`broadcast-monitor-input-${key}`}
                  />
                  <div className="flex items-center gap-1">
                    <button type="button"
                            onClick={saveEdit}
                            className="flex-1 px-2 py-1 rounded-full bg-black text-white text-[10px] font-bold inline-flex items-center justify-center gap-1"
                            data-testid={`broadcast-monitor-save-${key}`}>
                      <Save size={10} /> 저장
                    </button>
                    <button type="button"
                            onClick={cancelEdit}
                            className="px-2 py-1 rounded-full border border-black/10 dark:border-white/10 text-[10px] font-bold"
                            data-testid={`broadcast-monitor-cancel-${key}`}>
                      <Close size={10} />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${url ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${url ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-white/20'}`} />
                    {url ? (
                      <span data-testid={`broadcast-monitor-url-${key}`} title={url} className="truncate">{_LINKED_LABEL}</span>
                    ) : (
                      <span data-testid={`broadcast-monitor-empty-${key}`}>{_EMPTY_LABEL}</span>
                    )}
                  </span>
                  <div className="flex items-center gap-1">
                    <button type="button"
                            onClick={() => _safeOpen(url)}
                            disabled={!url}
                            className="flex-1 px-2 py-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold inline-flex items-center justify-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid={`broadcast-monitor-open-${key}`}>
                      방송 보기 <ExternalLink size={10} />
                    </button>
                    <button type="button"
                            onClick={() => beginEdit(key)}
                            className="px-1.5 py-1 rounded-full border border-black/10 dark:border-white/10 text-[10px] font-bold inline-flex items-center gap-1"
                            title="LIVE 주소 등록/수정"
                            data-testid={`broadcast-monitor-edit-${key}`}>
                      <Edit3 size={10} /> 주소
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
